import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canRead } from '@/lib/permissions';
import type { Tables, IntegrationProvider } from '@/types/database';

const KEY = ['integrations'];

/**
 * Integrations metadata (connection state, sync errors, realm IDs) is
 * gated to the integrations section — super_admin by default, or anyone
 * granted the section on Roles & users. Mirror that here so other pages
 * can't accidentally trigger a 403 query.
 */
export function useIntegrations() {
  const { role, profile, loading } = useAuth();
  const enabled = !loading && canRead(role, 'integrations', profile?.section_access);
  return useQuery({
    queryKey: KEY,
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('integrations')
        .select('*')
        .order('provider');
      if (error) throw error;
      return data as Tables<'integrations'>[];
    },
  });
}

export function useUpsertIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      provider: IntegrationProvider;
      status?: 'connected' | 'disconnected' | 'error' | 'syncing';
      metadata?: Record<string, unknown>;
    }) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .single();
      if (!profile) throw new Error('No profile');

      const { data, error } = await supabase
        .from('integrations')
        .upsert(
          {
            organization_id: profile.organization_id,
            provider: input.provider,
            status: input.status ?? 'connected',
            metadata: (input.metadata ?? {}) as never,
          },
          { onConflict: 'organization_id,provider' },
        )
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDisconnectIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (provider: IntegrationProvider) => {
      const { error } = await supabase
        .from('integrations')
        .update({ status: 'disconnected', metadata: {} })
        .eq('provider', provider);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export type SyncFunctionName =
  | 'mercury-sync'
  | 'qbo-sync'
  | 'plaid-sync'
  | 'web-analytics-sync'
  | 'shopify-sync'
  | 'amazon-sync'
  // Amazon Business buyer integration (ROADMAP §1.7, migration 61).
  // Pulls order + line-item data via the Business Reporting API once
  // the org's developer app is approved; CSV upload covers the gap.
  | 'amazon-business-sync'
  // Marketing — paid ads (migration 57). Each runs hourly on its own
  // cron schedule and writes into `ad_metrics_daily`.
  | 'amazon-ads-sync'
  | 'google-ads-sync'
  | 'meta-ads-sync'
  // Marketing — organic social (migration 57). Writes into
  // `social_post_metrics_daily`.
  | 'facebook-sync'
  | 'instagram-sync'
  | 'tiktok-sync'
  // Marketing Dashboard providers (migration 101). Search Console fills the
  // `search_console_*_daily` family; YouTube fills the same social tables as
  // the other organic platforms.
  | 'search-console-sync'
  | 'youtube-sync';

export function useTriggerSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (functionName: SyncFunctionName) => {
      return invokeEdgeFunction<{ ok: boolean }>(functionName, {});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['plaid-items'] });
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['web-analytics'] });
      qc.invalidateQueries({ queryKey: ['sales-orders'] });
      qc.invalidateQueries({ queryKey: ['sales-channels'] });
      qc.invalidateQueries({ queryKey: ['ad-accounts'] });
      qc.invalidateQueries({ queryKey: ['ad-metrics'] });
      qc.invalidateQueries({ queryKey: ['social-accounts'] });
      qc.invalidateQueries({ queryKey: ['social-post-metrics'] });
      qc.invalidateQueries({ queryKey: ['amazon-business-orders'] });
      // Every Marketing Dashboard hook is namespaced under 'marketing', so
      // one prefix invalidation refreshes all five tabs after any sync.
      qc.invalidateQueries({ queryKey: ['marketing'] });
    },
  });
}

/**
 * Lists connected ad accounts (Amazon Ads / Google Ads / Meta Ads / TikTok).
 * One row per ad-platform account; rendered under each provider's card on
 * the Integrations page so the operator can see which accounts are live.
 */
export function useAdAccounts(
  provider?: IntegrationProvider,
  opts: { enabled?: boolean } = {},
) {
  const { role, profile, loading } = useAuth();
  const enabled =
    !loading && canRead(role, 'integrations', profile?.section_access) && (opts.enabled ?? true);
  return useQuery({
    queryKey: ['ad-accounts', provider ?? 'all'],
    enabled,
    queryFn: async () => {
      let query = supabase.from('ad_accounts').select('*').order('created_at', { ascending: true });
      if (provider) query = query.eq('provider', provider);
      const { data, error } = await query;
      if (error) throw error;
      return data as Tables<'ad_accounts'>[];
    },
  });
}

/**
 * Lists connected social accounts (Facebook / Instagram / TikTok organic).
 * Same shape as `useAdAccounts` but for the organic-social side of the
 * marketing stack.
 */
export function useSocialAccounts(
  provider?: IntegrationProvider,
  opts: { enabled?: boolean } = {},
) {
  const { role, profile, loading } = useAuth();
  const enabled =
    !loading && canRead(role, 'integrations', profile?.section_access) && (opts.enabled ?? true);
  return useQuery({
    queryKey: ['social-accounts', provider ?? 'all'],
    enabled,
    queryFn: async () => {
      let query = supabase
        .from('social_accounts')
        .select('*')
        .order('created_at', { ascending: true });
      if (provider) query = query.eq('provider', provider);
      const { data, error } = await query;
      if (error) throw error;
      return data as Tables<'social_accounts'>[];
    },
  });
}

/**
 * Stores per-org marketing-OAuth *app* credentials (Client ID + Client
 * Secret, plus an optional `developer_token` for Google Ads). Same
 * pattern as `useSetShopifyAppCredentials` / `useSetAmazonAppCredentials`:
 * the public Client ID lands on `integrations.metadata.client_id`; the
 * secret is encrypted to `<provider>_client_secret_<org_id>` in
 * `integration_secrets`. After this resolves the UI flips the matching
 * card from "Configure app credentials" → "Connect account".
 *
 * Backed by one Edge Function per provider
 * (`<provider>-set-app-credentials`) because each platform has its own
 * Client ID / Client Secret format validation that's cleanest to keep
 * server-side.
 */
export function useSetMarketingAppCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      provider:
        | 'amazon_ads'
        | 'google_ads'
        | 'meta_ads'
        | 'facebook'
        | 'instagram'
        | 'tiktok';
      client_id: string;
      client_secret: string;
      // Optional. Required only for Google Ads, where the API additionally
      // requires a developer token alongside the OAuth client. The
      // matching Edge Function rejects empty values for that provider.
      developer_token?: string;
    }) => {
      const fnName = `${input.provider.replace('_', '-')}-set-app-credentials`;
      return invokeEdgeFunction<{ ok: boolean }>(fnName, {
        client_id: input.client_id,
        client_secret: input.client_secret,
        developer_token: input.developer_token,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Lists every Plaid Item connected to this org. One row per institution
 * login — i.e. AMEX is one item, Discover is another, even if they live
 * under the same `integrations.provider = 'plaid'` parent row.
 */
export function usePlaidItems() {
  const { role, profile, loading } = useAuth();
  const enabled = !loading && canRead(role, 'integrations', profile?.section_access);
  return useQuery({
    queryKey: ['plaid-items'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plaid_items')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as Tables<'plaid_items'>[];
    },
  });
}

/**
 * Triggers a sync for ONE Plaid item. Used by the per-item "Sync" / "Reconnect"
 * buttons on the Integrations page. Bulk syncs go through useTriggerSync().
 */
export function useSyncPlaidItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (plaidItemId: string) => {
      return invokeEdgeFunction<{ ok: boolean }>('plaid-sync', {
        plaid_item_id: plaidItemId,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plaid-items'] });
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Stores the org's Shopify *app* credentials (the dev-dashboard Client ID
 * + Client secret pair) ahead of the OAuth dance. The Client ID is public
 * and lives on `integrations.metadata.client_id`; the secret is encrypted
 * to `integration_secrets` under `shopify_client_secret_<org_id>`. After
 * this resolves the UI flips the Shopify card from "Configure app" mode
 * to "Connect a store" mode — the integration itself stays disconnected
 * until at least one store completes OAuth via `shopify-oauth-callback`.
 */
export function useSetShopifyAppCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { client_id: string; client_secret: string }) => {
      return invokeEdgeFunction<{ ok: boolean }>('shopify-set-app-credentials', {
        client_id: input.client_id,
        client_secret: input.client_secret,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Stores the org's Amazon SP-API *app* credentials (Application ID, LWA
 * Client ID, LWA Client secret) ahead of the OAuth dance. The two public
 * IDs land on `integrations.metadata`; the secret is encrypted to
 * `integration_secrets` under `amazon_client_secret_<org_id>`. After this
 * resolves the UI flips the Amazon card from "Configure app" mode to
 * "Connect a seller account" mode — the integration itself stays
 * disconnected until at least one seller completes OAuth via
 * `amazon-oauth-callback`.
 */
export function useSetAmazonAppCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      application_id: string;
      client_id: string;
      client_secret: string;
    }) => {
      return invokeEdgeFunction<{ ok: boolean }>('amazon-set-app-credentials', {
        application_id: input.application_id,
        client_id: input.client_id,
        client_secret: input.client_secret,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Connect an Amazon seller using a refresh token pasted from Seller
 * Central's self-authorization flow ("Authorization tokens" panel) —
 * skips the OAuth redirect dance entirely. Useful when the same Amazon
 * identity owns both the SP-API app and the seller account being
 * authorized, which is the common case for single-tenant Atlas installs.
 *
 * The Edge Function smoke-tests the token against
 * `/sellers/v1/marketplaceParticipations` so a bad paste fails fast with
 * a useful error message instead of silently producing zero-order syncs.
 */
export function useConnectAmazonWithToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      refresh_token: string;
      marketplace_id: string;
      seller_id: string;
      channel_name?: string;
    }) => {
      return invokeEdgeFunction<{
        ok: boolean;
        channel_id: string;
        selling_partner_id: string;
        marketplace_id: string;
        channel_name: string;
        marketplaces: Array<{ marketplace_id?: string; country_code?: string; participating?: boolean }>;
      }>('amazon-connect-with-token', {
        refresh_token: input.refresh_token,
        marketplace_id: input.marketplace_id,
        seller_id: input.seller_id,
        channel_name: input.channel_name,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['sales-channels'] });
      qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

/**
 * Stores an integration credential (e.g. Mercury API key, GA4 service-account
 * JSON) in Supabase Vault via the `integrations-set-secret` Edge Function and
 * marks the integration as connected. Pass `metadata` for providers that need
 * extra non-secret configuration (e.g. GA4's `property_id`); the server-side
 * function validates required-metadata keys per provider before flipping the
 * integration to 'connected'. Only callable by super_admins (enforced
 * server-side).
 */
export function useSaveIntegrationSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      provider: IntegrationProvider;
      secret: string;
      metadata?: Record<string, unknown>;
    }) => {
      return invokeEdgeFunction<{ ok: boolean }>('integrations-set-secret', {
        provider: input.provider,
        secret: input.secret,
        metadata: input.metadata,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Wrapper around `supabase.functions.invoke` that pulls the JSON `error`
 * field out of non-2xx responses so the UI can surface a useful message
 * instead of the generic "Edge Function returned a non-2xx status code".
 */
async function invokeEdgeFunction<T>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    const message = await extractFunctionErrorMessage(error);
    throw new Error(message);
  }
  return data as T;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as FunctionsHttpError).context as Response | undefined;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}
